import type { CSSProperties } from "react";

import { cn } from "../../lib/utils.js";
import { festiveLead, type ActiveFestival } from "../../lib/festive/getActiveFestival.js";
import { inkOnLight } from "../../lib/festive/ink.js";
import { useFestiveReducedMotion } from "../../hooks/useActiveFestival.js";

/**
 * Festival colours are config values, so they reach the CSS as custom properties on this root
 * (`.fi-lead` in tokens.css reads them). A static class cannot hold a per-festival colour.
 */
function festiveVars({ festival }: ActiveFestival): CSSProperties {
  return { "--fi-accent": festival.accent, "--fi-ink": inkOnLight(festival.accent) } as CSSProperties;
}

/**
 * One line of plain text under the dashboard title. The lead phrase is bold in the festival colour
 * and its colour drifts gently, a ✨ twinkles after it, and the message shimmers. The motion stops
 * for reduced-motion users. The line wraps below md and never truncates.
 */
export function FestiveLine({ active, className }: { active: ActiveFestival; className?: string }) {
  const reduce = useFestiveReducedMotion();
  return (
    <p
      className={cn(
        "fi-line -mt-2 w-full text-[11px] leading-4 text-surface-500 sm:text-[13px] sm:leading-5 whitespace-normal md:whitespace-nowrap",
        className,
      )}
      data-reduce={reduce ? "true" : "false"}
      style={festiveVars(active)}
    >
      <b className="fi-lead">{festiveLead(active)}</b>{" "}
      {/* Decorative: the sparkle is hidden from screen readers. */}
      <span className="fi-spark" aria-hidden="true">✨</span>{" "}
      <span className="fi-message">{active.festival.rest}</span>
    </p>
  );
}
